import SceneProps from "ui/editor/dialogs/scene-props/SceneProps";
import {EVENT_HISTORY_CHANGED, EVENT_SAVED} from "ui/canvas/Canvas";
import Guide from "ui/editor/dialogs/guide/Guide";
import LoginForm from "ui/components/login-form/LoginForm";
import {CurrentUser, ROLES} from "ui/CurrentUser";
import UserMenu from "ui/components/user-menu/UserMenu";
import {EVENT_TAKE_FRAME} from "./SceneEditorVideoRecorder";
import SceneEditor from "./SceneEditor";
import {formatFileSize, formatTimeInterval} from "ui/helpers/formatters";

/**
 * Scene Editor Top Panel Controller
 */
export default class SceneEditorTopPanel {
	/**
	 * @param {SceneEditor} editor
	 * @param {Element} cont
	 */
	constructor(editor, cont) {
		/**
		 * @type {SceneEditor}
		 * @private
		 */
		this._editor = editor;

		this._els = {
			buttons: {},
			userName: cont.querySelector('.editor-container__user-name'),
			loginButton: cont.querySelector('.button[data-role="signin"]'),
			userMenuButton: cont.querySelector('.button[data-role="user-menu"]'),
			saveMarkers: cont.querySelectorAll('.button__marker'),
			videoStat: cont.querySelector('.editor-container__recorder-stat'),
			videoFramesCounter: cont.querySelector('[data-role="video-frames-count"]'),
			videoFilesSize: cont.querySelector('[data-role="video-files-size"]'),
			videoDuration: cont.querySelector('[data-role="video-duration"]'),
			videoSequencesCounter: cont.querySelector('[data-role="video-sequences-count"]'),
		};

		this._handlers = {
			onButtonClick: this._onButtonClick.bind(this),
			updateSaveButton: this._updateSaveButton.bind(this),
			updateRecorderBlock: this._updateRecorderBlock.bind(this)
		};

		cont.querySelectorAll('.button[data-role]').forEach((el) => {
			this._els.buttons[el.getAttribute('data-role')] = el;
			el.addEventListener('click', this._handlers.onButtonClick)
		});

		this._els.userMenuButton.onclick = () => {
			new UserMenu(this._els.userMenuButton);
		};

		editor.canvas.events.addListener(EVENT_HISTORY_CHANGED, this._handlers.updateSaveButton);
		editor.canvas.events.addListener(EVENT_SAVED, this._handlers.updateSaveButton);
		editor.videoRecorder.events.addListener(EVENT_TAKE_FRAME, this._handlers.updateRecorderBlock);

		this._initUserSessionObserver();
		this.update();
	}

	/**
	 * Updates state of top panel
	 *
	 * @returns {SceneEditorTopPanel}
	 */
	update() {
		if (this._els.buttons['tools']) {
			this._els.buttons['tools'].classList.toggle('button_checked', this._editor.isToolsVisible());
		}
		return this;
	}

	/**
	 * @private
	 */
	_initUserSessionObserver() {
		CurrentUser.get().addObserver(() => {
			const user = CurrentUser.get();
			this._els.loginButton.hidden = user.isUser();
			this._els.userMenuButton.hidden = !user.isUser();
			this._els.userName.textContent = user.userName;
			this._els.userName.title = ROLES[user.userRole];
		});
	}

	/**
	 * Processes click by tool button
	 *
	 * @param {MouseEvent} e
	 * @private
	 */
	async _onButtonClick(e) {
		let el = e.target.closest('.button');
		switch (el.getAttribute('data-role')) {
			case 'signin':
				if (!CurrentUser.get().isUser()) {
					new LoginForm();
				}
				break;
			case 'tools':
				this._editor.toggleTools();
				break;
			case 'save':
				void this._editor.storage.save();
				break;
			case 'save-as-new':
				void this._editor.storage.save(true);
				break;
			case 'open':
				void this._editor.storage.open();
				break;
			case 'save-release':
				void this._editor.storage.release();
				break;
			case 'settings':
				this._openSettings();
				break;
			case 'preview':
				void this._editor.showPreview();
				break;
			case 'new':
				void this._editor.showPreview();
				break;
			case 'record-video':
				if (await this._editor.videoRecorder.start()) {
					el.hidden = true;
					this._els.buttons['stop-record-video'].hidden = false;
					this._els.videoStat.hidden = false;
					this._updateRecorderBlock();
				}
				break;
			case 'stop-record-video':
				el.hidden = true;
				this._els.buttons['record-video'].hidden = false;
				this._els.videoStat.hidden = true;
				this._editor.videoRecorder.stop();
				break;
			case 'guide':
				new Guide();
				break;
		}
	}

	/**
	 * Opens scene properties form
	 *
	 * @private
	 * @returns {void}
	 */
	_openSettings() {
		new SceneProps({canvas: this._editor.canvas});
	}

	/**
	 * @private
	 */
	_updateSaveButton() {
		const flag = this._editor.canvas.isStateSaved();
		this._els.saveMarkers.forEach(el => el.classList.toggle('button__marker_alert', !flag));
	}

	/**
	 * @private
	 */
	_updateRecorderBlock() {
		if (!this._els.videoFramesCounter) {
			return;
		}
		
		this._els.videoFramesCounter.textContent = `${this._editor.videoRecorder.framesCount}`;
		this._els.videoSequencesCounter.textContent = `${this._editor.videoRecorder.sequencesCount}`;
		this._els.videoFilesSize.textContent = `${formatFileSize(this._editor.videoRecorder.filesSize)}`;
		this._els.videoDuration.textContent = `${formatTimeInterval(this._editor.videoRecorder.duration)}`;
	}
}
